import React, { useState } from "react";
import { Button, Card, Col, Row, Nav, Modal } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { useExpense } from '../hooks/useExpense';
import { ExpenseForm } from '../components/Expense/ExpenseForm';
import { StatsCards } from '../components/Expense/StatsCards';
import { ExpenseTable } from '../components/Expense/ExpenseTable';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactApexChart from "react-apexcharts";
import { NoDataMessage } from '../components/common/NoDataMessage';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function Expense() {
    const [showModal, setShowModal] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [filter, setFilter] = useState('all');
    const {
        expenses,
        categories,
        loading,
        stats,
        addExpense,
        updateExpense,
        deleteExpense
    } = useExpense();

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedExpense(null);
    };

    const handleSubmit = async (values, formikHelpers) => {
        try {
            // console.log(values);
            const submitFn = selectedExpense ? updateExpense : addExpense;
            const result = await submitFn(values);
            
            if (result.success) {
                toast.success(result.message || 'Expense saved successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                });
                handleCloseModal();
            } else {
                toast.error(result.message || 'Failed to save expense', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                });
            }
        } catch (error) {
            console.error('Error saving expense:', error);
            toast.error('An error occurred while saving the expense', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            });
        } finally {
            formikHelpers.setSubmitting(false);
        }
    };

    const filteredExpenses = expenses.filter(expense => {
        if (filter === 'all') return true;
        if (filter === 'fixed') return expense.fixed;
        return !expense.fixed;
    });

    const getCategoryData = () => {
        const categoryTotals = {};
        expenses.forEach(expense => {
            const category = expense.category || 'Uncategorized';
            if (!categoryTotals[category]) {
                categoryTotals[category] = 0;
            }
            categoryTotals[category] += parseFloat(expense.amount || 0);
        });

        return {
            series: Object.values(categoryTotals),
            options: {
                chart: {
                    type: 'donut',
                },
                labels: Object.keys(categoryTotals),
                colors: ['#506fd9', '#85b6ff', '#33d685', '#0dcaf0', '#1c96e9', '#6e7985'],
                legend: {
                    position: 'bottom'
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total',
                                    formatter: function (w) {
                                        return '$' + w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(2);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        };
    };

    const getTypeData = () => {
        const fixedAmount = expenses.filter(e => e.fixed).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
        const variableAmount = expenses.filter(e => !e.fixed).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

        return {
            series: [fixedAmount, variableAmount],
            options: {
                chart: {
                    type: 'donut',
                },
                labels: ['Fixed', 'Variable'],
                colors: ['#506fd9', '#85b6ff'],
                legend: {
                    position: 'bottom'
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total',
                                    formatter: function (w) {
                                        return '$' + w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(2);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        };
    };

    const getTrendData = () => {
        const monthlyData = expenses.reduce((acc, expense) => {
            const month = new Date(expense.date).toLocaleString('default', { month: 'short' });
            acc[month] = (acc[month] || 0) + Number(expense.amount || 0);
            return acc;
        }, {});

        const hasData = Object.keys(monthlyData).length > 0;

        return {
            series: [{
                name: 'Expenses',
                data: hasData ? Object.values(monthlyData) : [0]
            }],
            options: {
                chart: {
                    type: 'area',
                    height: 350,
                    toolbar: {
                        show: false
                    }
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                xaxis: {
                    categories: hasData ? Object.keys(monthlyData) : ['No Data']
                },
                colors: ['#506fd9'],
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.7,
                        opacityTo: 0.3,
                        stops: [0, 90, 100]
                    }
                },
            }
        };
    };

    const chartOptions = {
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    generateLabels: (chart) => {
                        const datasets = chart.data.datasets;
                        if (datasets.length === 0) return [];
                        
                        const data = datasets[0].data;
                        const total = data.reduce((sum, val) => sum + val, 0);
                        
                        return chart.data.labels.map((label, i) => ({
                            total: total ? `$${total.toFixed(2)}` : 'No Data',
                            text: label,
                            fillStyle: datasets[0].backgroundColor[i],
                            hidden: false,
                            lineCap: 'butt',
                            lineDash: [],
                            lineDashOffset: 0,
                            lineJoin: 'miter',
                            lineWidth: 1,
                            strokeStyle: datasets[0].backgroundColor[i],
                            pointStyle: 'circle',
                            datasetIndex: 0,
                            index: i
                        }));
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const value = context.raw;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `$${value.toFixed(2)} (${percentage}%)`;
                    }
                }
            }
        },
        cutout: '70%',
        maintainAspectRatio: false,
        responsive: true
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <Footer />
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h4 className="main-title mb-0">Expenses</h4>
                    </div>
                    <Button 
                        variant="primary" 
                        onClick={() => setShowModal(true)}
                    >
                        Add New Expense
                    </Button>
                </div>

                <StatsCards stats={stats} />

                <Card className="card-one mb-4">
                    <Card.Body>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <Nav className="nav-pills">
                                <Nav.Link
                                    active={filter === 'all'}
                                    onClick={() => setFilter('all')}
                                >All</Nav.Link>
                                <Nav.Link
                                    active={filter === 'fixed'}
                                    onClick={() => setFilter('fixed')}
                                >Fixed</Nav.Link>
                                <Nav.Link
                                    active={filter === 'variable'}
                                    onClick={() => setFilter('variable')}
                                >Variable</Nav.Link>
                            </Nav>
                        </div>

                        <ExpenseTable 
                            expenses={filteredExpenses}
                            onDelete={deleteExpense}
                            onEdit={(expense) => {
                                setSelectedExpense(expense);
                                setShowModal(true);
                            }}
                        />
                    </Card.Body>
                </Card>

                <Row className="g-3">
                    <Col md={4}>
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">By Category</Card.Title>
                            </Card.Header>
                            <Card.Body style={{ height: '300px' }}>
                                {expenses.length === 0 ? (
                                    <NoDataMessage 
                                        icon="ri-pie-chart-line"
                                        message="No expense data available"
                                    />
                                ) : (
                                    <ReactApexChart
                                        options={getCategoryData().options}
                                        series={getCategoryData().series}
                                        type="donut"
                                        height="100%"
                                    />
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Fixed vs Variable</Card.Title>
                            </Card.Header>
                            <Card.Body style={{ height: '300px' }}>
                                {expenses.length === 0 ? (
                                    <NoDataMessage 
                                        icon="ri-bar-chart-line"
                                        message="No expense data available"
                                    />
                                ) : (
                                    <ReactApexChart
                                        options={getTypeData().options}
                                        series={getTypeData().series}
                                        type="donut"
                                        height="100%"
                                    />
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Expense Trends</Card.Title>
                            </Card.Header>
                            <Card.Body style={{ height: '300px' }}>
                                {expenses.length === 0 ? (
                                    <NoDataMessage 
                                        icon="ri-line-chart-line"
                                        message="No expense data available"
                                    />
                                ) : (
                                    <ReactApexChart
                                        options={getTrendData().options}
                                        series={getTrendData().series}
                                        type="area"
                                        height="100%"
                                    />
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Modal show={showModal} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedExpense ? 'Edit Expense' : 'Add New Expense'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ExpenseForm
                            categories={categories}
                            initialValues={selectedExpense}
                            onSubmit={handleSubmit}
                            onCancel={handleCloseModal}
                        />
                    </Modal.Body>
                </Modal>

                <Footer />
            </div>
        </React.Fragment>
    );
} 